"use client"

import { useState, useEffect } from "react"
import { cn } from "@/lib/utils"

interface HolidayUpvoteButtonProps {
  initialUpvoted?: boolean
  initialCount?: number
  theme?: "christmas" | "halloween" | "fourth-of-july"
  onUpvote?: (upvoted: boolean, newCount: number) => void
  className?: string
  disabled?: boolean
}

export function HolidayUpvoteButton({
  initialUpvoted = false,
  initialCount = 0,
  theme = "christmas",
  onUpvote,
  className,
  disabled = false,
}: HolidayUpvoteButtonProps) {
  const [isUpvoted, setIsUpvoted] = useState(initialUpvoted)
  const [count, setCount] = useState(initialCount)
  const [isAnimating, setIsAnimating] = useState(false)

  // Update state when props change (important for parent state updates)
  useEffect(() => {
    setIsUpvoted(initialUpvoted)
    setCount(initialCount)
  }, [initialUpvoted, initialCount])

  const handleUpvote = () => {
    if (disabled) return
    
    setIsAnimating(true)
    const newUpvoted = !isUpvoted
    const newCount = newUpvoted ? count + 1 : count - 1

    setIsUpvoted(newUpvoted)
    setCount(newCount)
    onUpvote?.(newUpvoted, newCount)

    setTimeout(() => setIsAnimating(false), 300)
  }

  const ChristmasIcon = ({ filled }: { filled: boolean }) =>
    filled ? (
      <span className={cn("text-xl transition-all duration-300", isAnimating && "scale-125")}>🎄</span>
    ) : (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        className={cn("transition-all duration-300", isAnimating && "scale-125")}
      >
        {/* Christmas Tree */}
        <path
          d="M12 2L8 8h8l-4-6z"
          fill={filled ? "#22c55e" : "none"}
          stroke={filled ? "#22c55e" : "currentColor"}
          strokeWidth="2"
          className="transition-colors duration-300"
        />
        <path
          d="M12 6L7 12h10l-5-6z"
          fill={filled ? "#16a34a" : "none"}
          stroke={filled ? "#16a34a" : "currentColor"}
          strokeWidth="2"
          className="transition-colors duration-300"
        />
        <path
          d="M12 10L6 16h12l-6-6z"
          fill={filled ? "#15803d" : "none"}
          stroke={filled ? "#15803d" : "currentColor"}
          strokeWidth="2"
          className="transition-colors duration-300"
        />
        {/* Tree trunk */}
        <rect
          x="10"
          y="16"
          width="4"
          height="4"
          fill={filled ? "#92400e" : "none"}
          stroke={filled ? "#92400e" : "currentColor"}
          strokeWidth="2"
          className="transition-colors duration-300"
        />
        {/* Star on top */}
        <path
          d="M12 1l1 2h2l-1.5 1.5L14.5 7 12 5.5 9.5 7l1-2.5L9 3h2l1-2z"
          fill={filled ? "#fbbf24" : "none"}
          stroke={filled ? "#fbbf24" : "currentColor"}
          strokeWidth="1"
          className="transition-colors duration-300"
        />
      </svg>
    )

  const HalloweenIcon = ({ filled }: { filled: boolean }) =>
    filled ? (
      <span className={cn("text-xl transition-all duration-300", isAnimating && "scale-125")}>🎃</span>
    ) : (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        className={cn("transition-all duration-300", isAnimating && "scale-125")}
      >
        {/* Pumpkin body with ridges */}
        <ellipse
          cx="12"
          cy="13"
          rx="8"
          ry="6"
          fill={filled ? "#f97316" : "none"}
          stroke={filled ? "#f97316" : "currentColor"}
          strokeWidth="3"
          className="transition-colors duration-300"
        />
        {/* Pumpkin ridges for more realistic look */}
        <path
          d="M8 8c0 4 0 8 0 10"
          stroke={filled ? "#ea580c" : "currentColor"}
          strokeWidth="2"
          className="transition-colors duration-300"
        />
        <path
          d="M12 7c0 4 0 8 0 12"
          stroke={filled ? "#ea580c" : "currentColor"}
          strokeWidth="2"
          className="transition-colors duration-300"
        />
        <path
          d="M16 8c0 4 0 8 0 10"
          stroke={filled ? "#ea580c" : "currentColor"}
          strokeWidth="2"
          className="transition-colors duration-300"
        />
        {/* Pumpkin stem - thicker and more prominent */}
        <path
          d="M11 7c0-2 1-3 2-3s2 1 2 3"
          fill="none"
          stroke={filled ? "#16a34a" : "currentColor"}
          strokeWidth="3"
          strokeLinecap="round"
          className="transition-colors duration-300"
        />
        {/* Eyes - triangular and more prominent */}
        <path d="M9 11l2 0-1 2z" fill={filled ? "#000" : "currentColor"} className="transition-colors duration-300" />
        <path d="M15 11l2 0-1 2z" fill={filled ? "#000" : "currentColor"} className="transition-colors duration-300" />
        {/* Mouth - jagged smile */}
        <path
          d="M8 15l1-1 1 1 1-1 1 1 1-1 1 1 1-1 1 1"
          fill="none"
          stroke={filled ? "#000" : "currentColor"}
          strokeWidth="2.5"
          strokeLinecap="round"
          className="transition-colors duration-300"
        />
      </svg>
    )

  const FourthOfJulyIcon = ({ filled }: { filled: boolean }) => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      className={cn("transition-all duration-300", isAnimating && "scale-125")}
    >
      {/* Main star */}
      <path
        d="M12 2l3 6h6l-5 4 2 6-6-4-6 4 2-6-5-4h6l3-6z"
        fill={filled ? "#dc2626" : "none"}
        stroke={filled ? "#dc2626" : "currentColor"}
        strokeWidth="2"
        className="transition-colors duration-300"
      />
      {/* Inner star with blue */}
      <path
        d="M12 5l2 4h4l-3 2.5 1 4-4-2.5-4 2.5 1-4-3-2.5h4l2-4z"
        fill={filled ? "#2563eb" : "none"}
        stroke={filled ? "#2563eb" : "currentColor"}
        strokeWidth="1.5"
        className="transition-colors duration-300"
      />
      {/* Center white star */}
      <path
        d="M12 7l1.5 3h3l-2.5 2 1 3-3-2-3 2 1-3-2.5-2h3l1.5-3z"
        fill={filled ? "#ffffff" : "none"}
        stroke={filled ? "#ffffff" : "currentColor"}
        strokeWidth="1"
        className="transition-colors duration-300"
      />
      {/* Firework sparkles */}
      {filled && (
        <>
          <circle cx="6" cy="6" r="1" fill="#fbbf24" className="animate-pulse" />
          <circle cx="18" cy="6" r="1" fill="#fbbf24" className="animate-pulse animation-delay-100" />
          <circle cx="6" cy="18" r="1" fill="#fbbf24" className="animate-pulse animation-delay-200" />
          <circle cx="18" cy="18" r="1" fill="#fbbf24" className="animate-pulse animation-delay-300" />
        </>
      )}
    </svg>
  )

  const getButtonStyles = () => {
    if (theme === "christmas") {
      return isUpvoted
        ? "bg-gradient-to-r from-red-500 to-green-500 text-white shadow-lg shadow-red-500/25"
        : "bg-gray-100 hover:bg-red-50 text-gray-700 border border-gray-200 hover:border-red-200"
    } else if (theme === "halloween") {
      return isUpvoted
        ? "bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-lg shadow-orange-500/25"
        : "bg-gray-100 hover:bg-orange-50 text-gray-700 border border-gray-200 hover:border-orange-200"
    } else {
      return isUpvoted
        ? "bg-blue-600 text-white shadow-lg shadow-blue-500/25 border-2 border-red-400"
        : "bg-gray-100 hover:bg-blue-50 text-gray-700 border border-gray-200 hover:border-blue-200"
    }
  }

  const getCounterStyles = () => {
    if (isUpvoted) {
      if (theme === "christmas") {
        return "bg-red-700 text-white"
      } else if (theme === "halloween") {
        return "bg-orange-700 text-white"
      } else {
        // Fourth of July theme
        return "bg-blue-700 text-white"
      }
    } else {
      return "bg-gray-800 text-gray-100"
    }
  }

  const renderIcon = () => {
    if (theme === "christmas") {
      return <ChristmasIcon filled={isUpvoted} />
    } else if (theme === "halloween") {
      return <HalloweenIcon filled={isUpvoted} />
    } else {
      return <FourthOfJulyIcon filled={isUpvoted} />
    }
  }

  const getSparkleColor = () => {
    if (theme === "christmas") {
      return "bg-yellow-300"
    } else if (theme === "halloween") {
      return "bg-orange-300"
    } else {
      return "bg-blue-300"
    }
  }

  return (
    <button
      onClick={handleUpvote}
      disabled={disabled}
      className={cn(
        "inline-flex items-center gap-2 px-4 py-2 rounded-full font-medium transition-all duration-300 hover:scale-105 active:scale-95 relative",
        getButtonStyles(),
        disabled && "opacity-50 cursor-not-allowed hover:scale-100",
        className,
      )}
    >
      {renderIcon()}

      <span className="text-sm font-semibold">{isUpvoted ? "Upvoted" : "Upvote"}</span>

      <span
        className={cn("text-xs px-2 py-0.5 rounded-full transition-all duration-300 font-medium", getCounterStyles())}
      >
        {count} {count === 1 ? "point" : "points"}
      </span>

      {/* Sparkle animation when upvoted */}
      {isUpvoted && (
        <div className="absolute inset-0 pointer-events-none">
          <div className={cn("absolute top-0 right-0 w-1 h-1 rounded-full animate-ping", getSparkleColor())} />
          <div
            className={cn(
              "absolute bottom-0 left-0 w-1 h-1 rounded-full animate-ping animation-delay-150",
              getSparkleColor(),
            )}
          />
        </div>
      )}
    </button>
  )
}